/**
 * components/object/ObjectRelations.jsx — Iter 48 Related Objects.
 *
 * Drops into any inspector. Single fetch of /api/objects/{type}/{id}/
 * related, categorised display, each row offers a Jump action that
 * dispatches the `forgedops:jump` event the viewer consumes.
 *
 * No graph viz. No dashboard. Just operational hyperlinks the field
 * user taps to navigate without leaving the drawing.
 */
import React, { useEffect, useState } from "react";
import {
  Layers3, MapPin, GitBranch, Ruler, Eye, AlertCircle, Camera,
  ChevronRight, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import { dispatchJump } from "@/lib/jumpTo";

const SECTIONS = [
  { key: "source_object",           label: "Source",                  icon: GitBranch },
  { key: "nearby_structures",       label: "Nearby Structures",       icon: Layers3 },
  { key: "nearby_pins",             label: "Related Pins",            icon: MapPin },
  { key: "affected_revisions",      label: "Affected Revisions",      icon: GitBranch },
  { key: "connected_utility_watches", label: "Utility Watches",       icon: Eye },
  { key: "related_measurements",    label: "Measurements",            icon: Ruler },
  { key: "recent_photos",           label: "Recent Photos",           icon: Camera },
  { key: "open_issues",             label: "Open Issues",             icon: AlertCircle },
];

export default function ObjectRelations({ objectType, objectId, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data: body } = await api.get(
          `/objects/${objectType}/${objectId}/related`);
        if (!cancelled) setData(body?.related || {});
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load relationships");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [objectType, objectId, refreshKey]);

  if (loading) {
    return (
      <div className="text-[var(--fop-text-3)] text-xs flex items-center gap-2"
            data-testid="object-relations-loading">
        <Loader2 size={12} className="animate-spin" /> Resolving relationships…
      </div>
    );
  }
  if (err) {
    return (
      <div className="text-[var(--fop-danger)] text-xs"
            data-testid="object-relations-error">{err}</div>
    );
  }
  if (!data) return null;

  // Compute non-empty sections; if none, show a calm hint.
  const present = SECTIONS.filter(s => (data[s.key] || []).length > 0);
  if (present.length === 0) {
    return (
      <div className="text-[var(--fop-text-3)] text-xs text-center py-6"
            data-testid="object-relations-empty">
        No related objects yet. As you add notes, photos, and watches,
        this section fills in automatically.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="object-relations">
      <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] flex items-center gap-1.5">
        <GitBranch size={12} /> Related Objects
      </div>
      {present.map(s => (
        <RelationSection key={s.key} title={s.label} Icon={s.icon}
                          entries={data[s.key]} />
      ))}
    </div>
  );
}

function RelationSection({ title, Icon, entries }) {
  return (
    <div className="fop-surface rounded-md p-2.5" data-testid={`relation-section-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1.5">
        <Icon size={10} /> {title} <span className="text-[var(--fop-text-4)]">({entries.length})</span>
      </div>
      <ul className="space-y-1">
        {entries.map((e, i) => (
          <RelationRow key={`${e.object_type}:${e.object_id}:${i}`} ev={e} />
        ))}
      </ul>
    </div>
  );
}

function RelationRow({ ev }) {
  const kindBadge = ev.relationship_kind || "derived";
  const sub = renderSubline(ev);
  return (
    <li>
      <button
        className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-[var(--fop-surface-3)] transition-colors group"
        data-testid={`relation-jump-${ev.object_type}-${ev.object_id}`}
        onClick={() => dispatchJump(ev)}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[12px] text-[var(--fop-text)] font-medium truncate">
            {ev.label || ev.object_id}
          </div>
          {sub && (
            <div className="text-[10px] text-[var(--fop-text-3)] font-mono uppercase tracking-wider truncate">
              {sub}
            </div>
          )}
        </div>
        <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] px-1.5 py-0.5 rounded border border-[var(--fop-border)]"
               title={`${kindBadge} relationship`}>
          {kindBadge}
        </span>
        <ChevronRight size={12} className="text-[var(--fop-text-4)] group-hover:text-[var(--fop-primary)] transition-colors" />
      </button>
    </li>
  );
}

function renderSubline(ev) {
  const x = ev.extra || {};
  if (ev.object_type === "structure") {
    return [x.install_status, x.conflict !== "normal" && x.conflict]
      .filter(Boolean).join(" · ") || ev.object_type;
  }
  if (ev.object_type === "pin") {
    return [x.category, x.status].filter(Boolean).join(" · ");
  }
  if (ev.object_type === "revision") {
    return [x.discipline, x.is_current ? "current" : "superseded"]
      .filter(Boolean).join(" · ");
  }
  if (ev.object_type === "measurement") {
    return [x.kind, x.value != null ? `${x.value}` : null]
      .filter(Boolean).join(" · ");
  }
  if (ev.object_type === "utility_watch") {
    return x.layer_type || ev.object_type;
  }
  if (ev.object_type === "issue") {
    return [x.status, x.reopened_count > 0 ? `reopened ${x.reopened_count}×` : null]
      .filter(Boolean).join(" · ");
  }
  if (ev.object_type === "photo") {
    return x.origin_type ? `from ${x.origin_type}` : null;
  }
  return null;
}
